<template>
	<div>
		<user-nav-menu bg-color="transparent" text-color="#ffffff" :logo-file-name="tenant.settings.brandImages.headerLogos.app.light"></user-nav-menu>
		<v-container class="mt-16">
			<v-row class="mb-8">
				<v-col class="text-center white--text">
					<h1>Bookings Search</h1>
				</v-col>
			</v-row>

			<v-row>
				<v-col offset-md="8" cols="12" md="2">
					<v-menu bottom left>
						<template #activator="{ on, attrs }">
							<v-btn dark block v-bind="attrs" v-on="on"> Export </v-btn>
						</template>

						<v-list>
							<v-list-item>
								<v-list-item-title :disabled="!exportData.length">
									<vue-json-to-csv :json-data="exportData" :labels="exportFieldLabels" csv-title="BookingsExport">
										<v-btn text :disabled="!exportData.length">Locumbase Export</v-btn>
									</vue-json-to-csv>
								</v-list-item-title>
							</v-list-item>
							<v-list-item>
								<v-list-item-title :disabled="!exportData.length">
									<vue-json-to-csv :json-data="exportDataAHC" :labels="exportFieldLabelsAHC" csv-title="BookingsExport">
										<v-btn text>AHC Export</v-btn>
									</vue-json-to-csv>
								</v-list-item-title>
							</v-list-item>
						</v-list>
					</v-menu>
				</v-col>
				<v-col cols="12" md="2">
					<v-btn dark color="green" block :loading="loadingData" class="mr-4" @click="searchDialog = true"> Search </v-btn>
				</v-col>
			</v-row>

			<v-data-table :headers="tableHeaders" :items="filteredData" item-key="id" :loading="loadingData" class="elevation-0 mt-2">
				<!-- eslint-disable-next-line -->
				<template v-slot:item.actions="{ item }">
					<v-icon disabled small class="mr-2"> mdi-eye </v-icon>
				</template>
			</v-data-table>

			<v-row class="mt-8">
				<v-col class="text-center white--text">
					<v-btn
						class="pt-0 mt-0"
						link
						depressed
						style="
							color: var(--v-primary-base);
							font-family: Montserrat;
							font-style: normal;
							font-weight: normal;
							font-size: 14px;
							line-height: 17px;
							text-align: right;
							text-decoration-line: underline;
						"
						to="/"
					>
						return to dashboard
					</v-btn>
				</v-col>
			</v-row>
		</v-container>

		<!-- Search Dialog -->
		<v-dialog v-model="searchDialog" persistent max-width="500px">
			<v-card>
				<v-card-title>
					<span class="headline">Search</span>
				</v-card-title>
				<v-card-text>
					<v-container>
						<v-form v-model="validSearchForm">
							<v-row>
								<v-col cols="12" class="pa-2">
									<div class="text-center">{{ dateRangeText }}</div>
								</v-col>
								<v-col cols="12" class="d-flex justify-center">
									<v-date-picker v-model="searchRange" range color="primary" elevation="2"></v-date-picker>
								</v-col>
								<v-col cols="12">
									<v-text-field v-model="selectedCompany.id" label="Company Id" :rules="[v => !!v || 'Field is required']"></v-text-field>
								</v-col>
							</v-row>
						</v-form>
					</v-container>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn text :disabled="loadingData" @click="closeSearchDialog"> Close </v-btn>
					<v-btn :disabled="!validSearch" color="primary" outlined :loading="loadingData" @click="searchData()"> Search </v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Pending Invites Dialog -->
		<v-dialog v-model="pendingInviteDialog" persistent scrollable max-width="600px">
			<v-card>
				<v-card-title>
					<span class="headline">Search</span>
				</v-card-title>
				<v-card-text id="pending-invite-wrapper">
					<v-list two-line>
						<template v-if="pendingInvites.length > 0">
							<template v-for="(pendingInvite, i) in pendingInvites">
								<v-divider :key="i" />

								<v-list-item :key="pendingInvite.id">
									<v-list-item-action>
										<v-btn icon :loading="loadingData" @click="cancelPendingInvite(pendingInvite)">
											<v-icon color="grey lighten-1"> mdi-delete </v-icon>
										</v-btn>
									</v-list-item-action>
									<v-list-item-content>
										<v-list-item-title>{{ pendingInvite.email }}</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
							</template>
						</template>
						<template v-else>
							<v-list-item> <v-list-item-subtitle>No pending invites</v-list-item-subtitle> </v-list-item>
						</template>
					</v-list>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn color="blue darken-1" :loading="loadingData" text @click="pendingInviteDialog = false"> Close </v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-snackbar v-model="failedInvite" top>
			Failed to create invite
			<template #action="{ attrs }">
				<v-btn text v-bind="attrs" @click="failedInvite = false"> Close </v-btn>
			</template>
		</v-snackbar>

		<v-snackbar v-model="failedInviteCancel" top>
			Failed to cancel invite
			<template #action="{ attrs }">
				<v-btn text v-bind="attrs" @click="failedInviteCancel = false"> Close </v-btn>
			</template>
		</v-snackbar>
	</div>
</template>

<script>
import { mapActions } from 'vuex';
import UserNavMenu from '@/components/user/UserNavMenu';
import VueJsonToCsv from 'vue-json-to-csv';
import { Invite } from '@/models/user.model';
import { Company } from '@/models/company.model';
import moment from 'moment';

export default {
	name: 'CompanyUserManagement',

	components: {
		UserNavMenu,
		VueJsonToCsv,
	},

	data() {
		return {
			loadingData: false,
			loadingUsers: false,
			searchDialog: false,
			pendingInviteDialog: false,
			failedInviteCancel: false,
			failedInvite: false,
			validSearchForm: false,
			pendingInvites: [],
			searchRange: [],
			invite: new Invite(),
			selectedCompany: new Company(),
			searchResults: [],
			tableHeaders: [
				{ text: 'Actions', value: 'actions', sortable: false },
				{ text: 'Company', value: 'companyName' },
				{ text: 'Professional', value: 'professionalName' },
				{ text: 'Start Date', value: 'start_date' },
				{ text: 'End Date', value: 'end_date' },
				{ text: 'Code', value: 'unique_booking_code' },
				{ text: 'Status', value: 'status' },
			],
			emailRules: [v => !!v || 'E-mail is required', v => /.+@.+\..+/.test(v) || 'E-mail must be valid'],
			exportFieldLabels: {
				id: { title: 'Id' },
				companyName: { title: 'Company' },
				companyId: { title: 'Company Id' },
				professionalName: { title: 'Professional' },
				professionalId: { title: 'Professional Id' },
				companyEmail: { title: 'Company Email' },
				professionalEmail: { title: 'Professional Email' },
				industry: { title: 'Industry' },
				profession: { title: 'Profession' },
				skills: { title: 'Skills' },
				rates: { title: 'Rates' },
				status: { title: 'Status' },
				unique_booking_code: { title: 'Unique Code' },
				startDate: { title: 'Start Date' },
				endDate: { title: 'End Date' },
				startTime: { title: 'Start Time' },
				endTime: { title: 'End Time' },
				booking_dates: { title: 'Booking Dates' },
			},
			exportFieldLabelsAHC: {
				id: { title: 'Id' },
				companyName: { title: 'Company' },
				companyId: { title: 'Company Id' },
				professionalName: { title: 'Professional' },
				professionalId: { title: 'Professional Id' },
				companyEmail: { title: 'Company Email' },
				professionalEmail: { title: 'Professional Email' },
				industry: { title: 'Industry' },
				profession: { title: 'Profession' },
				skills: { title: 'Skills' },
				status: { title: 'Status' },
				unique_booking_code: { title: 'Unique Code' },
				startDate: { title: 'Start Date' },
				endDate: { title: 'End Date' },
				startTime: { title: 'Start Time' },
				endTime: { title: 'End Time' },
				dateCode: { title: 'Date Code' },
			},
		};
	},

	computed: {
		filteredData() {
			const results = this.normalisedData;
			const filteredResults = [];

			if (this.nameSearch) {
				const nameSearch = this.nameSearch.toLowerCase();
				filteredResults.push(...results.filter(x => x.professionalName.toString().toLowerCase().includes(nameSearch)));
				return filteredResults;
			}

			return results;
		},

		normalisedData() {
			return this.searchResults.map(x => ({
				id: x.id,
				companyName: x.company.name,
				professionalName: x.professional.name,
				start_date: x.start_date,
				end_date: x.end_date,
				status: x.status,
				unique_booking_code: x.unique_booking_code,
				fullObj: x,
			}));
		},

		exportData() {
			return this.searchResults.map(x => ({
				id: x.id,
				companyName: x.company.name,
				companyId: x.company.id,
				professionalName: x.professional.name,
				professionalId: x.professional.id,
				companyEmail: x.company.email,
				professionalEmail: x.professional.email,
				industry: x.company.industry,
				profession: x.professional.profession,
				skills: x.professional.skills,
				rates: Object.values(x.professional.rates).join(),
				status: x.status,
				unique_booking_code: x.unique_booking_code,
				startDate: x.start_date,
				endDate: x.end_date,
				startTime: x.start_time,
				endTime: x.end_time,
				booking_dates: x.booking_dates,
				request_dates: x.request_dates,
			}));
		},

		searchResultsComputed() {
			return this.exportData.map(x => ({
				...x,
				start_time_iso: moment(`${x.start_date} ${x.start_time}`).format(),
				end_time_iso: moment(`${x.end_date} ${x.end_time}`).format(),
			}));
		},

		exportDataAHC() {
			const results = [];
			for (let booking of this.exportData) {
				if (!booking.request_dates) continue;

				for (let bookingDate of booking.request_dates) {
					const startDate = moment(bookingDate.start_time);
					const endDate = moment(bookingDate.end_time);

					const dateCode = this.calcDateCode(startDate.format());

					results.push({
						...booking,
						start_date: startDate.format(),
						start_time: startDate.format('HH:mm'),
						end_date: endDate.format(),
						end_time: endDate.format('HH:mm'),
						dateCode,
					});
				}
			}

			return results;
		},

		dateRangeText() {
			if (this.searchRange.length === 2) {
				return this.searchRange.join(' ~ ');
			}
			if (this.searchRange.length === 1) {
				return `${this.searchRange[0]} ~ Select second date`;
			}
			return 'Select date range';
		},

		dateRangeValid() {
			return this.searchRange.length === 2;
		},

		validSearch() {
			return this.validSearchForm && this.dateRangeValid;
		},

		companyManagers() {
			if (!this.company || !this.company.managers) return [];

			return this.company.managers;
		},
	},

	mounted() {
		this.getCompany();
	},

	methods: {
		...mapActions('company', ['InviteCompanyUser', 'GetPendingCompanyUserInvites', 'CancelCompanyUserInvite', 'LoadCompany']),

		...mapActions('bookings', ['LoadCompanyBookings']),

		calcDateCode(startDate) {
			const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
			const normalDays = [0, 1, 2, 3, 4, 5].map(x => weekDays[x]);
			const normalNights = [6, 0, 1, 2, 3, 4].map(x => weekDays[x]);
			const nightStartTime = 19;

			startDate = moment(startDate);

			const day = startDate.format('dddd');
			const startHour = startDate.hour();

			const publicHolidays = [];

			if (startHour < nightStartTime) {
				/* Day shift */
				if (publicHolidays.includes(startDate.format('YYYY-MM-DD'))) {
					return 'PUBLIC_HOLIDAY';
				}

				if (normalDays.includes(day)) {
					return 'NORMAL_DAY';
				} else {
					return 'SUNDAY_DAY';
				}
			} else {
				/* Night shift */
				if (publicHolidays.includes(startDate.format('YYYY-MM-DD'))) {
					return 'PUBLIC_HOLIDAY_NIGHT';
				}

				if (normalNights.includes(day)) {
					return 'NORMAL_NIGHT';
				} else {
					return 'SATURDAY_DAY';
				}
			}
		},

		async searchData() {
			this.loadingData = true;

			const dates = this.searchRange.slice();
			dates.sort((a, b) => +new Date(a) - +new Date(b));

			const startDate = dates[0];
			const endDate = dates[1];

			const results = await this.LoadCompanyBookings({
				accountId: this.selectedCompany.id.trim(),
				startDate,
				endDate,
			});

			this.searchResults = results;

			this.loadingData = false;
		},

		async getPendingInvites() {
			const invites = await this.GetPendingCompanyUserInvites();
			if (!invites || invites.length === 0) {
				this.pendingInvites = [];
				return;
			}

			this.pendingInvites = invites;
		},

		async showPendingInvites() {
			await this.getPendingInvites();
			this.pendingInviteDialog = true;
		},

		closeSearchDialog() {
			this.searchDialog = false;
		},

		resetInvite() {
			this.invite = new Invite();
		},

		async cancelPendingInvite(invite) {
			this.loadingData = true;

			await this.CancelCompanyUserInvite(invite.id);
			await this.getPendingInvites();

			this.loadingData = false;
		},

		async getCompany() {
			const company = await this.LoadCompany();
			this.company = company;
		},
	},
};
</script>

<style></style>
